<template>
  <v-container>
    <v-row>
      <v-col cols="12" v-if="currentAnnualReview">
        <v-card>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-card-title class="d-block">
                <v-icon class="mr-4" x-large color="secondary">event</v-icon>
                {{ currentAnnualReview.annualReviewStartDate | year }} Annual
                Review For {{ user.fullName }}
              </v-card-title>
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-row>
            <v-col cols="12" sm="4" class="mr-n1">
              <v-card-text>
                <v-row class="justify-center">
                  <v-col cols="auto">
                    <user-avatar
                      :size="65"
                      :user="user"
                      borderColor="performance"
                      color="performance"
                      cssClass="halo text-h3"
                      textColor="white"
                    ></user-avatar>
                  </v-col>
                  <v-col cols="auto">
                    <div class="secondary--text font-weight-medium subtitle-1">
                      {{ userHeaderText }}
                    </div>
                    <div>
                      <v-btn
                        outlined
                        color="lighttext"
                        v-if="currentAnnualReview.userQuestionsComplete"
                        :to="
                          `/annual-reviews/${user.id}/${currentAnnualReview.id}/coreboard`
                        "
                      >
                        <v-icon color="success" class="mr-2"
                          >check_circle</v-icon
                        >
                        Completed
                      </v-btn>
                      <v-btn
                        color="performance"
                        v-else-if="isProfileUser"
                        :to="
                          `/annual-reviews/${user.id}/${currentAnnualReview.id}/coreboard`
                        "
                      >
                        Start Your Review
                      </v-btn>
                      <v-btn color="lighttext" outlined v-else>
                        <v-icon class="mr-2">pause_circle</v-icon>
                        Waiting on {{ firstName }}
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-col>
            <v-divider vertical></v-divider>
            <v-col cols="12" sm="4" class="mx-n1">
              <v-card-text>
                <v-row class="justify-center">
                  <v-col cols="auto">
                    <user-avatar
                      :size="65"
                      :user="currentAnnualReview.manager"
                      borderColor="performance"
                      color="performance"
                      cssClass="halo text-h3"
                      textColor="white"
                    ></user-avatar>
                  </v-col>
                  <v-col cols="auto">
                    <div class="secondary--text font-weight-medium subtitle-1">
                      {{ managerHeaderText }}
                    </div>
                    <div>
                      <v-btn
                        outlined
                        color="lighttext"
                        v-if="currentAnnualReview.managerQuestionsComplete"
                        :to="
                          `/annual-reviews/${user.id}/${currentAnnualReview.id}/coreboard`
                        "
                      >
                        <v-icon color="success" class="mr-2"
                          >check_circle</v-icon
                        >
                        Completed
                      </v-btn>
                      <v-btn
                        color="performance"
                        v-else-if="!isProfileUser"
                        :to="
                          `/annual-reviews/${user.id}/${currentAnnualReview.id}/coreboard`
                        "
                      >
                        Start Your Review
                      </v-btn>
                      <v-btn outlined color="lighttext" v-else>
                        Not Completed
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-col>
            <v-divider vertical></v-divider>
            <v-col cols="12" sm="4" class="ml-n1">
              <v-card-text>
                <v-row class="justify-center">
                  <v-col cols="auto" class="mr-8 mt-3">
                    <v-badge color="transparent" offset-y="-10" offset-x="20">
                      <template v-slot:badge="{}">
                        <user-avatar
                          :size="50"
                          :user="currentAnnualReview.manager"
                          color="performance"
                          cssClass="text-h4"
                          textColor="white"
                        ></user-avatar>
                      </template>
                      <user-avatar
                        :size="50"
                        :user="user"
                        color="performance"
                        cssClass="text-h4"
                        textColor="white"
                      ></user-avatar>
                    </v-badge>
                  </v-col>
                  <v-col cols="auto">
                    <div class="secondary--text font-weight-medium subtitle-1">
                      Review With {{ firstName }}
                    </div>
                    <div>
                      <v-btn
                        outlined
                        color="lighttext"
                        v-if="
                          currentAnnualReview.managerSignoffComplete &&
                            currentAnnualReview.userSignoffComplete
                        "
                        :to="
                          `/annual-reviews/${user.id}/${currentAnnualReview.id}/coreboard`
                        "
                      >
                        <v-icon color="success" class="mr-2"
                          >check_circle</v-icon
                        >
                        Signed Off
                      </v-btn>
                      <v-btn
                        color="performance"
                        v-else-if="
                          currentAnnualReview.managerQuestionsComplete &&
                            currentAnnualReview.userQuestionsComplete
                        "
                        :to="
                          `/annual-reviews/${user.id}/${currentAnnualReview.id}/coreboard`
                        "
                      >
                        Review
                      </v-btn>
                      <v-btn outlined color="lighttext" v-else>
                        <v-icon color="lighttext" class="mr-2">cancel</v-icon>
                        Not Ready
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-card>
          <v-card-title
            >History of {{ firstName }}'s Annual Reviews</v-card-title
          >
          <v-card-text>
            <v-data-table
              :loading="loading"
              :headers="headers"
              :items="list"
              :no-data-text="`${firstName} has no historical Annual Reviews`"
              :footer-props="{
                'items-per-page-options': [10, 25, 50, 100]
              }"
            >
              <template v-slot:header.date="{}">
                <v-icon>event</v-icon>
              </template>
              <template v-slot:item.date="{ item }">
                {{ item.annualReviewEndDate | normalDate }}
              </template>
              <template v-slot:item.manager="{ item }">
                <user-avatar :size="32" :user="item.manager"></user-avatar>
                {{ item.manager.fullName }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn
                  text
                  class="light-background"
                  small
                  color="info"
                  :to="`/annual-reviews/${user.id}/${item.id}/coreboard`"
                >
                  View
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "AnnualReviewUserList",
  props: { user: { type: Object } },
  components: {},
  data: () => ({
    loading: false,
    hideTable: false,
    headers: [
      { text: "", value: "date" },
      { text: "Manager", value: "manager" },
      { text: "Bonus", value: "bonus" },
      { text: "Pay", value: "pay" },
      { text: "Rating", value: "rating" },
      { text: "Review", value: "actions", sortable: false }
    ]
  }),
  watch: {
    user(val, oldVal) {
      if (val == oldVal) {
        return;
      }
      this.getData();
    }
  },
  computed: {
    userHeaderText() {
      return (
        (this.isProfileUser ? "Your" : `${this.firstName}'s`) + " Self Review"
      );
    },
    managerHeaderText() {
      return this.isProfileUser
        ? "Your Manager's Review Of You"
        : `Your Review Of ${this.firstName}`;
    },
    firstName() {
      return this.user.firstName || this.user.email;
    },
    isProfileUser() {
      return this.currentAnnualReview.user.id == this.profile.id;
    },
    currentAnnualReview() {
      return this.history.find(x => !x.complete);
    },
    list() {
      return this.history.filter(x => x.complete);
    },
    ...mapGetters({
      history: "annualreview/history",
      profile: "profile/profile"
    })
  },
  created() {
    this.getData();
  },
  methods: {
    getData() {
      if (this._.isEmpty(this.user)) {
        return;
      }
      this.loading = true;
      let callArray = [];
      callArray.push(
        this.$store.dispatch(
          "annualreview/getAnnualReviewHistory",
          this.user.id
        )
      );
      Promise.all(callArray)
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading annual reviews."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>
